import * as XLSX from 'xlsx';
import { SiteInsert } from '@/types/site';
import { ZoneInsert } from '@/types/zone';
import { SystemInsert } from '@/types/system';
import { EndpointInsert } from '@/types/endpoint';
import { CloudServiceInsert } from '@/types/cloudService';
import { InternetConnectionInsert } from '@/types/internetConnection';
import { DMZConfigInsert, FirewallRuleInsert } from '@/types/dmzConfig';
import { isValidIP, isValidCIDR, isValidMAC, isValidVLAN, isValidSubnetMask, isValidGateway, formatMAC } from '@/utils/networkValidation';

export interface ValidationError {
  sheet: string;
  row: number;
  column: string;
  message: string;
  severity: 'error' | 'warning';
}

// Extended types with duplicate detection metadata and temp IDs for parent-child linking
export type ParsedSite = SiteInsert & { 
  _isExisting?: boolean; 
  _existingId?: string; 
  _pendingOwnerEmail?: string;
  _tempId?: string; // Temp ID for new items during validation
};
export type ParsedCloudService = CloudServiceInsert & { 
  _isExisting?: boolean; 
  _existingId?: string;
  _tempId?: string;
};
export type ParsedZone = ZoneInsert & { 
  _isExisting?: boolean; 
  _existingId?: string;
  _tempId?: string;
  _parentTempId?: string; // Track if parent is also new (temp ID)
};
export type ParsedSystem = SystemInsert & { 
  _isExisting?: boolean; 
  _existingId?: string; 
  _pendingOwnerEmail?: string;
  _tempId?: string;
  _parentTempId?: string;
};
export type ParsedEndpoint = EndpointInsert & { 
  _isExisting?: boolean; 
  _existingId?: string;
  _parentTempId?: string;
};
export type ParsedInternetConnection = InternetConnectionInsert & {
  _isExisting?: boolean;
  _existingId?: string;
  _tempId?: string;
  _parentTempId?: string;
  org_id: string;
};
export type ParsedDMZConfig = DMZConfigInsert & {
  _isExisting?: boolean;
  _existingId?: string;
  _tempId?: string;
  _parentTempId?: string;
  org_id: string;
};
export type ParsedFirewallRule = FirewallRuleInsert & {
  _isExisting?: boolean;
  _existingId?: string;
  _dmzTempId?: string;
  org_id: string;
};

export interface ParsedAssets {
  sites: ParsedSite[];
  cloudServices: ParsedCloudService[];
  zones: ParsedZone[];
  systems: ParsedSystem[];
  endpoints: ParsedEndpoint[];
  internetConnections: ParsedInternetConnection[];
  dmzConfigs: ParsedDMZConfig[];
  firewallRules: ParsedFirewallRule[];
}

export interface ImportSummary {
  total: number;
  new: number;
  existing: number;
}

export interface PendingOwnerInfo {
  email: string;
  entities: { type: string; name: string }[];
}

export interface SiteMissingCoordinates {
  id: string;
  name: string;
  address?: string;
  _tempId?: string;
  _isExisting?: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  preview: ParsedAssets;
  summary: {
    sites: ImportSummary;
    cloudServices: ImportSummary;
    zones: ImportSummary;
    systems: ImportSummary;
    endpoints: ImportSummary;
    internetConnections: ImportSummary;
    dmzConfigs: ImportSummary;
    firewallRules: ImportSummary;
  };
  pendingOwners: PendingOwnerInfo[];
  sitesWithoutCoordinates: SiteMissingCoordinates[];
}

export interface OrgMember {
  email: string;
  user_id: string;
  name?: string | null;
}

// Helper for case-insensitive name matching with available options for error messages
function findByName<T extends { name: string; id?: string; _tempId?: string }>(
  items: T[], 
  searchName: string
): { match: T | undefined; availableNames: string[] } {
  const search = searchName.toLowerCase().trim();
  const availableNames = items.map(i => i.name);
  
  // Exact match first (case-insensitive)
  const exact = items.find(i => i.name.toLowerCase().trim() === search);
  if (exact) return { match: exact, availableNames };
  
  // Partial match fallback (if only one result)
  const partial = items.filter(i => 
    i.name.toLowerCase().includes(search) || 
    search.includes(i.name.toLowerCase())
  );
  if (partial.length === 1) return { match: partial[0], availableNames };
  
  return { match: undefined, availableNames };
}

// Helper for case-insensitive label matching (for DMZ configs, Internet connections)
function findByLabel<T extends { label: string; id?: string; _tempId?: string }>(
  items: T[], 
  searchLabel: string
): { match: T | undefined; availableLabels: string[] } {
  const search = searchLabel.toLowerCase().trim();
  const availableLabels = items.map(i => i.label);
  
  // Exact match first (case-insensitive)
  const exact = items.find(i => i.label.toLowerCase().trim() === search);
  if (exact) return { match: exact, availableLabels };
  
  // Partial match fallback (if only one result)
  const partial = items.filter(i => 
    i.label.toLowerCase().includes(search) || 
    search.includes(i.label.toLowerCase())
  );
  if (partial.length === 1) return { match: partial[0], availableLabels };
  
  return { match: undefined, availableLabels };
}

// Generate a temp ID for new items (prefixed to distinguish from real UUIDs)
function generateTempId(): string {
  return `temp_${crypto.randomUUID()}`;
}

// Parse Excel file and validate
export async function parseExcelFile(
  file: File,
  existingAssets: {
    sites: any[];
    cloudServices: any[];
    zones: any[];
    systems: any[];
    endpoints: any[];
    internetConnections?: any[];
    dmzConfigs?: any[];
    firewallRules?: any[];
  },
  orgId: string,
  orgMembers: OrgMember[] = []
): Promise<ValidationResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const errors: ValidationError[] = [];
        const warnings: ValidationError[] = [];
        const sitesWithoutCoordinates: SiteMissingCoordinates[] = [];
        const preview: ParsedAssets = {
          sites: [],
          cloudServices: [],
          zones: [],
          systems: [],
          endpoints: [],
          internetConnections: [],
          dmzConfigs: [],
          firewallRules: [],
        };
        
        // Track pending owners across all sheets
        const pendingOwnersMap = new Map<string, { type: string; name: string }[]>();
        
        // Parse Sites
        if (workbook.SheetNames.includes('Sites')) {
          const sheet = workbook.Sheets['Sites'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const { assets, errors: sheetErrors, warnings: sheetWarnings, pendingOwners, sitesWithoutCoords } = parseSitesSheet(rows, orgId, existingAssets.sites, orgMembers);
          preview.sites = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
          sitesWithoutCoordinates.push(...sitesWithoutCoords);
          
          // Aggregate pending owners
          pendingOwners.forEach(po => {
            const existing = pendingOwnersMap.get(po.email) || [];
            existing.push(...po.entities);
            pendingOwnersMap.set(po.email, existing);
          });
        }
        
        // Parse Cloud Services
        if (workbook.SheetNames.includes('Cloud Services')) {
          const sheet = workbook.Sheets['Cloud Services'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseCloudServicesSheet(rows, orgId, existingAssets.cloudServices);
          preview.cloudServices = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }
        
        // Parse Zones (requires sites for parent lookup)
        if (workbook.SheetNames.includes('Zones')) {
          const sheet = workbook.Sheets['Zones'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allSites = [...existingAssets.sites, ...preview.sites];
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseZonesSheet(rows, orgId, existingAssets.zones, allSites);
          preview.zones = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }
        
        // Parse Systems (requires zones for parent lookup)
        if (workbook.SheetNames.includes('Systems')) {
          const sheet = workbook.Sheets['Systems'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allZones = [...existingAssets.zones, ...preview.zones];
          const { assets, errors: sheetErrors, warnings: sheetWarnings, pendingOwners } = parseSystemsSheet(rows, orgId, existingAssets.systems, allZones, orgMembers);
          preview.systems = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
          
          // Aggregate pending owners
          pendingOwners.forEach(po => {
            const existing = pendingOwnersMap.get(po.email) || [];
            existing.push(...po.entities);
            pendingOwnersMap.set(po.email, existing);
          });
        }
        
        // Parse Endpoints (requires systems for parent lookup)
        if (workbook.SheetNames.includes('Endpoints')) {
          const sheet = workbook.Sheets['Endpoints'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allSystems = [...existingAssets.systems, ...preview.systems];
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseEndpointsSheet(rows, orgId, existingAssets.endpoints, allSystems);
          preview.endpoints = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }

        // Parse Internet Connections
        if (workbook.SheetNames.includes('Internet Connections')) {
          const sheet = workbook.Sheets['Internet Connections'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allSites = [...existingAssets.sites, ...preview.sites];
          const allZones = [...existingAssets.zones, ...preview.zones];
          const allSystems = [...existingAssets.systems, ...preview.systems];
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseInternetConnectionsSheet(
            rows, orgId, existingAssets.internetConnections || [], allSites, allZones, allSystems
          );
          preview.internetConnections = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }

        // Parse DMZ Configurations
        if (workbook.SheetNames.includes('DMZ Configurations')) {
          const sheet = workbook.Sheets['DMZ Configurations'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allSites = [...existingAssets.sites, ...preview.sites];
          const allZones = [...existingAssets.zones, ...preview.zones];
          const allSystems = [...existingAssets.systems, ...preview.systems];
          const allCloudServices = [...existingAssets.cloudServices, ...preview.cloudServices];
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseDMZConfigsSheet(
            rows, orgId, existingAssets.dmzConfigs || [], allSites, allZones, allSystems, allCloudServices
          );
          preview.dmzConfigs = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }

        // Parse Firewall Rules (requires DMZ configs for parent lookup)
        if (workbook.SheetNames.includes('Firewall Rules')) {
          const sheet = workbook.Sheets['Firewall Rules'];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          const allDMZConfigs = [...(existingAssets.dmzConfigs || []), ...preview.dmzConfigs];
          const { assets, errors: sheetErrors, warnings: sheetWarnings } = parseFirewallRulesSheet(
            rows, orgId, existingAssets.firewallRules || [], allDMZConfigs
          );
          preview.firewallRules = assets;
          errors.push(...sheetErrors);
          warnings.push(...sheetWarnings);
        }
        
        // Calculate summary with new vs existing counts
        const countSummary = (items: Array<{ _isExisting?: boolean }>) => ({
          total: items.length,
          new: items.filter(i => !i._isExisting).length,
          existing: items.filter(i => i._isExisting).length,
        });

        const summary = {
          sites: countSummary(preview.sites),
          cloudServices: countSummary(preview.cloudServices),
          zones: countSummary(preview.zones),
          systems: countSummary(preview.systems),
          endpoints: countSummary(preview.endpoints),
          internetConnections: countSummary(preview.internetConnections),
          dmzConfigs: countSummary(preview.dmzConfigs),
          firewallRules: countSummary(preview.firewallRules),
        };
        
        // Convert pending owners map to array
        const pendingOwners: PendingOwnerInfo[] = Array.from(pendingOwnersMap.entries()).map(([email, entities]) => ({
          email,
          entities,
        }));
        
        resolve({
          isValid: errors.length === 0,
          errors,
          warnings,
          preview,
          summary,
          pendingOwners,
          sitesWithoutCoordinates,
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}

// Helper to validate email format
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Helper to find org member by email
function findOrgMember(email: string, orgMembers: OrgMember[]): OrgMember | undefined {
  return orgMembers.find(m => m.email.toLowerCase() === email.toLowerCase());
}

// Parse Sites sheet
function parseSitesSheet(rows: any[], orgId: string, existingSites: any[], orgMembers: OrgMember[]) {
  const assets: ParsedSite[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  const pendingOwners: PendingOwnerInfo[] = [];
  const sitesWithoutCoords: SiteMissingCoordinates[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2; // Account for header row
    
    // Skip empty rows
    if (!row['Site Name'] && !row.name) return;
    
    const name = (row['Site Name'] || row.name || '').toString().trim();
    if (!name) return;
    
    // Validate required fields
    if (!name) {
      errors.push({ sheet: 'Sites', row: rowNum, column: 'name', message: 'Name is required', severity: 'error' });
      return;
    }
    
    // Parse country code (handle "SE - Sweden" format from dropdown)
    let countryCode = (row['Country Code'] || row.country || '').toString().trim();
    if (countryCode.includes(' - ')) {
      countryCode = countryCode.split(' - ')[0];
    }
    
    // Validate country code
    if (countryCode && countryCode.length !== 2) {
      errors.push({ sheet: 'Sites', row: rowNum, column: 'country', message: 'Country must be 2-letter code', severity: 'error' });
    }
    
    // Validate criticality (5-level scale)
    const criticality = row['Criticality'] || row.criticality;
    if (criticality && !['Very High', 'High', 'Medium', 'Low', 'Very Low'].includes(criticality)) {
      errors.push({ sheet: 'Sites', row: rowNum, column: 'criticality', message: 'Criticality must be Very High, High, Medium, Low, or Very Low', severity: 'error' });
    }
    
    // Handle owner field
    const ownerEmail = (row['Owner Email'] || row.owner || '').toString().trim();
    let ownerId: string | undefined;
    let pendingOwnerEmail: string | undefined;
    
    if (ownerEmail) {
      if (!isValidEmail(ownerEmail)) {
        errors.push({ sheet: 'Sites', row: rowNum, column: 'owner', message: 'Invalid email format', severity: 'error' });
      } else {
        const member = findOrgMember(ownerEmail, orgMembers);
        if (member) {
          ownerId = member.user_id;
        } else {
          // Queue for pending invitation
          pendingOwnerEmail = ownerEmail;
          pendingOwners.push({ email: ownerEmail, entities: [{ type: 'site', name }] });
          warnings.push({ sheet: 'Sites', row: rowNum, column: 'owner', message: `User "${ownerEmail}" not in org - will queue for invitation`, severity: 'warning' });
        }
      }
    }
    
    // Check for existing site by name (case-insensitive)
    const existingSite = existingSites.find(s => 
      s.name.toLowerCase() === name.toLowerCase()
    );
    
    // Generate temp ID for new sites so child entities can reference them
    const tempId = existingSite ? undefined : generateTempId();
    
    // Check for coordinates - for BOTH new and existing sites
    const existingMetadata = existingSite?.metadata;
    const hasCoordinates = existingMetadata?.coordinates?.lat && existingMetadata?.coordinates?.lon;
    if (!hasCoordinates) {
      // Track site for coordinates review after import (new OR existing)
      sitesWithoutCoords.push({
        id: existingSite?.id || tempId!,
        name,
        address: row['Address'] || row.address || existingSite?.address || undefined,
        _tempId: existingSite ? undefined : tempId,
        _isExisting: !!existingSite,
      });
      warnings.push({ 
        sheet: 'Sites', 
        row: rowNum, 
        column: 'Coordinates', 
        message: existingSite 
          ? 'Existing site has no GPS coordinates - can be set after import'
          : 'New site has no GPS coordinates - can be set after import', 
        severity: 'warning' 
      });
    }
    
    const site: ParsedSite = {
      id: existingSite?.id || tempId, // Use existing ID or temp ID for validation
      org_id: orgId,
      name,
      site_code: row['Site Code'] || row.site_code || undefined,
      address: row['Address'] || row.address || undefined,
      country: countryCode || undefined,
      timezone: row['Timezone'] || row.timezone || undefined,
      criticality: criticality || undefined,
      owner_id: ownerId,
      notes: row['Notes'] || row.notes || undefined,
      metadata: row.tags ? { tags: String(row.tags).split(';').map((t: string) => t.trim()) } : undefined,
      _isExisting: !!existingSite,
      _existingId: existingSite?.id,
      _pendingOwnerEmail: pendingOwnerEmail,
      _tempId: tempId,
    };
    
    const rowId = row['ID (auto-generated)'] || row.id;
    if (rowId && !rowId.startsWith('temp_')) site.id = rowId;
    assets.push(site);
  });
  
  return { assets, errors, warnings, pendingOwners, sitesWithoutCoords };
}

// Parse Cloud Services sheet
function parseCloudServicesSheet(rows: any[], orgId: string, existingCloudServices: any[]) {
  const assets: ParsedCloudService[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const name = (row['Service Name'] || row.name || '').toString().trim();
    if (!name) return;
    
    const provider = row['Provider'] || row.provider;
    
    if (!name || !provider) {
      errors.push({ sheet: 'Cloud Services', row: rowNum, column: 'name/provider', message: 'Name and Provider are required', severity: 'error' });
      return;
    }
    
    if (!['AWS', 'Azure', 'GCP', 'Oracle', 'IBM', 'Other'].includes(provider)) {
      errors.push({ sheet: 'Cloud Services', row: rowNum, column: 'provider', message: 'Invalid provider', severity: 'error' });
    }
    
    // Check for existing cloud service by name + provider
    const existingService = existingCloudServices.find(c => 
      c.name.toLowerCase() === name.toLowerCase() &&
      c.provider === provider
    );
    
    // Generate temp ID for new cloud services
    const tempId = existingService ? undefined : generateTempId();
    
    const cloudService: ParsedCloudService = {
      id: existingService?.id || tempId,
      org_id: orgId,
      name,
      provider,
      account_id: row['Account ID'] || row.account_id || undefined,
      subscription_id: row['Subscription ID'] || row.subscription_id || undefined,
      project_id: row['Project ID'] || row.project_id || undefined,
      region: row['Region'] || row.region || undefined,
      environment: row['Environment'] || row.environment || undefined,
      criticality: row['Criticality'] || row.criticality || undefined,
      notes: row['Notes'] || row.notes || undefined,
      tags: row.tags ? String(row.tags).split(';').map((t: string) => t.trim()) : undefined,
      _isExisting: !!existingService,
      _existingId: existingService?.id,
      _tempId: tempId,
    };
    
    const rowId = row['ID (auto-generated)'] || row.id;
    if (rowId && !rowId.startsWith('temp_')) cloudService.id = rowId;
    assets.push(cloudService);
  });
  
  return { assets, errors, warnings };
}

// Parse Zones sheet - zones only belong to sites (no cloud_service_id)
function parseZonesSheet(rows: any[], orgId: string, existingZones: any[], allSites: any[]) {
  const assets: ParsedZone[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const name = (row['Zone Name'] || row.name || '').toString().trim();
    if (!name) return;
    
    const domain = row['Domain'] || row.domain;
    
    if (!name || !domain) {
      errors.push({ sheet: 'Zones', row: rowNum, column: 'name/domain', message: 'Name and Domain are required', severity: 'error' });
      return;
    }
    
    // Resolve parent site
    let siteId: string | undefined;
    let parentTempId: string | undefined;
    
    // Support both new format (Parent Site) and legacy (parent_site, parent_name with parent_type='site')
    const parentSite = row['Parent Site'] || row.parent_site || (row.parent_type === 'site' ? row.parent_name : undefined);
    
    if (parentSite) {
      const { match, availableNames } = findByName(allSites, parentSite);
      if (match) {
        siteId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        const available = availableNames.length > 0 ? ` Available sites: ${availableNames.slice(0, 5).join(', ')}${availableNames.length > 5 ? '...' : ''}` : '';
        errors.push({ sheet: 'Zones', row: rowNum, column: 'parent_site', message: `Site "${parentSite}" not found.${available}`, severity: 'error' });
      }
    } else if (row.parent_id && row.parent_type === 'site') {
      // Legacy format with direct parent_id
      siteId = row.parent_id;
    }
    if (!siteId) {
      errors.push({ sheet: 'Zones', row: rowNum, column: 'parent_site', message: 'Zone must have a parent site', severity: 'error' });
      return;
    }
    
    // Check for existing zone by name + site (only match against real IDs, not temp IDs)
    const existingZone = existingZones.find(z => 
      z.name.toLowerCase() === name.toLowerCase() &&
      siteId && !siteId.startsWith('temp_') && z.site_id === siteId
    );
    
    // Generate temp ID for new zones
    const tempId = existingZone ? undefined : generateTempId();
    
    const cidrs = row['CIDRs'] || row.cidrs;
    const vlanIdRaw = row['VLAN ID'] || row.vlan_id;
    
    // Validate CIDRs
    if (cidrs) {
      const cidrList = String(cidrs).split(';').map((c: string) => c.trim());
      cidrList.forEach((cidr: string) => {
        if (cidr && !isValidCIDR(cidr)) {
          warnings.push({ sheet: 'Zones', row: rowNum, column: 'cidrs', message: `Invalid CIDR format: "${cidr}"`, severity: 'warning' });
        }
      });
    }
    
    // Validate VLAN ID
    let vlanId: number | undefined;
    if (vlanIdRaw) {
      const vlanNum = parseInt(vlanIdRaw, 10);
      if (isValidVLAN(vlanNum)) {
        vlanId = vlanNum;
      } else {
        warnings.push({ sheet: 'Zones', row: rowNum, column: 'vlan_id', message: `Invalid VLAN ID: "${vlanIdRaw}" (must be 1-4094)`, severity: 'warning' });
      }
    }
    
    const zone: ParsedZone = {
      id: existingZone?.id || tempId,
      org_id: orgId,
      name,
      site_id: siteId,
      domain,
      purdue_level: row['Purdue Level'] || row.purdue_level || undefined,
      cidrs: cidrs ? String(cidrs).split(';').map((c: string) => c.trim()) : undefined,
      vlan_id: vlanId,
      data_classification: row['Data Classification'] || row.data_classification || undefined,
      backup_frequency: row['Backup Frequency'] || row.backup_frequency || undefined,
      criticality: row['Criticality'] || row.criticality || undefined,
      description: row['Notes'] || row.notes || undefined,
      _isExisting: !!existingZone,
      _existingId: existingZone?.id,
      _tempId: tempId,
      _parentTempId: parentTempId,
    };
    
    const rowId = row['ID (auto-generated)'] || row.id;
    if (rowId && !rowId.startsWith('temp_')) zone.id = rowId;
    assets.push(zone);
  });
  
  return { assets, errors, warnings };
}

// Parse Systems sheet
function parseSystemsSheet(rows: any[], orgId: string, existingSystems: any[], allZones: any[], orgMembers: OrgMember[]) {
  const assets: ParsedSystem[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  const pendingOwners: PendingOwnerInfo[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const name = (row['System Name'] || row.name || '').toString().trim();
    if (!name) return;
    
    if (!name) {
      errors.push({ sheet: 'Systems', row: rowNum, column: 'name', message: 'Name is required', severity: 'error' });
      return;
    }
    
    // Resolve parent zone - support both new format (parent_zone) and legacy (parent_name/parent_id)
    let zoneId: string | undefined;
    let parentTempId: string | undefined;
    const parentZone = row['Parent Zone'] || row.parent_zone;
    
    if (parentZone) {
      const { match, availableNames } = findByName(allZones, parentZone);
      if (match) {
        zoneId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        const available = availableNames.length > 0 ? ` Available zones: ${availableNames.slice(0, 5).join(', ')}${availableNames.length > 5 ? '...' : ''}` : '';
        errors.push({ sheet: 'Systems', row: rowNum, column: 'parent_zone', message: `Zone "${parentZone}" not found.${available}`, severity: 'error' });
      }
    } else if (row.parent_id) {
      zoneId = row.parent_id;
    } else if (row.parent_name) {
      const { match, availableNames } = findByName(allZones, row.parent_name);
      if (match) {
        zoneId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        const available = availableNames.length > 0 ? ` Available: ${availableNames.slice(0, 5).join(', ')}` : '';
        errors.push({ sheet: 'Systems', row: rowNum, column: 'parent_name', message: `Zone "${row.parent_name}" not found.${available}`, severity: 'error' });
      }
    }
    
    if (!zoneId) {
      errors.push({ sheet: 'Systems', row: rowNum, column: 'parent', message: 'System must have a parent zone', severity: 'error' });
      return;
    }
    
    // Handle owner field
    const ownerEmail = (row['Owner Email'] || row.owner || '').toString().trim();
    let owner: string | undefined;
    let pendingOwnerEmail: string | undefined;
    
    if (ownerEmail) {
      if (!isValidEmail(ownerEmail)) {
        errors.push({ sheet: 'Systems', row: rowNum, column: 'owner', message: 'Invalid email format', severity: 'error' });
      } else {
        const member = findOrgMember(ownerEmail, orgMembers);
        if (member) {
          owner = member.email; // Systems use owner (text) not owner_id
        } else {
          // Queue for pending invitation
          pendingOwnerEmail = ownerEmail;
          owner = ownerEmail; // Still store the email, will be resolved later
          pendingOwners.push({ email: ownerEmail, entities: [{ type: 'system', name }] });
          warnings.push({ sheet: 'Systems', row: rowNum, column: 'owner', message: `User "${ownerEmail}" not in org - will queue for invitation`, severity: 'warning' });
        }
      }
    }
    
    // Check for existing system by name + parent zone (only match real IDs)
    const existingSystem = existingSystems.find(s => 
      s.name.toLowerCase() === name.toLowerCase() &&
      zoneId && !zoneId.startsWith('temp_') && s.zone_id === zoneId
    );
    
    // Generate temp ID for new systems
    const tempId = existingSystem ? undefined : generateTempId();
    
    const protocols = row['Protocols'] || row.protocols;
    const piiTypes = row['PII Types'] || row.pii_types;
    const failoverTested = row['Failover Tested'] || row.failover_tested;
    const processesPii = row['Processes PII'] || row.processes_pii;
    const dpiaRequired = row['DPIA Required'] || row.dpia_required;
    
    // Network fields with validation
    const cidr = row['CIDR'] || row.cidr;
    const vlanIdRaw = row['VLAN ID'] || row.vlan_id;
    const defaultGateway = row['Default Gateway'] || row.default_gateway;
    
    if (cidr && !isValidCIDR(cidr)) {
      warnings.push({ sheet: 'Systems', row: rowNum, column: 'cidr', message: `Invalid CIDR format: "${cidr}"`, severity: 'warning' });
    }
    
    let vlanId: number | undefined;
    if (vlanIdRaw) {
      const vlanNum = parseInt(vlanIdRaw, 10);
      if (isValidVLAN(vlanNum)) {
        vlanId = vlanNum;
      } else {
        warnings.push({ sheet: 'Systems', row: rowNum, column: 'vlan_id', message: `Invalid VLAN ID: "${vlanIdRaw}" (must be 1-4094)`, severity: 'warning' });
      }
    }
    
    if (defaultGateway && !isValidGateway(defaultGateway)) {
      warnings.push({ sheet: 'Systems', row: rowNum, column: 'default_gateway', message: `Invalid gateway IP: "${defaultGateway}"`, severity: 'warning' });
    }
    
    const system: ParsedSystem = {
      id: existingSystem?.id || tempId,
      org_id: orgId,
      zone_id: zoneId,
      name,
      function: row['Function'] || row.function || undefined,
      cidr: cidr || undefined,
      vlan_id: vlanId,
      default_gateway: defaultGateway || undefined,
      protocols: protocols ? String(protocols).split(';').map((p: string) => p.trim()) : undefined,
      rto: row['RTO (hours)'] || row.rto ? Number(row['RTO (hours)'] || row.rto) : undefined,
      rpo: row['RPO (hours)'] || row.rpo ? Number(row['RPO (hours)'] || row.rpo) : undefined,
      failover_tested: failoverTested ? failoverTested.toString().toUpperCase() === 'TRUE' : undefined,
      processes_pii: processesPii ? processesPii.toString().toUpperCase() === 'TRUE' : undefined,
      pii_types: piiTypes ? String(piiTypes).split(';').map((t: string) => t.trim()) : undefined,
      dpia_required: dpiaRequired ? dpiaRequired.toString().toUpperCase() === 'TRUE' : undefined,
      owner,
      _isExisting: !!existingSystem,
      _existingId: existingSystem?.id,
      _pendingOwnerEmail: pendingOwnerEmail,
      _tempId: tempId,
      _parentTempId: parentTempId,
    };
    
    const rowId = row['ID (auto-generated)'] || row.id;
    if (rowId && !rowId.startsWith('temp_')) system.id = rowId;
    assets.push(system);
  });
  
  return { assets, errors, warnings, pendingOwners };
}

// Parse Endpoints sheet
function parseEndpointsSheet(rows: any[], orgId: string, existingEndpoints: any[], allSystems: any[]) {
  const assets: ParsedEndpoint[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const name = (row['Endpoint Name'] || row.name || '').toString().trim();
    if (!name) return;
    
    if (!name) {
      errors.push({ sheet: 'Endpoints', row: rowNum, column: 'name', message: 'Name is required', severity: 'error' });
      return;
    }
    
    // Resolve parent system - support both new format (parent_system) and legacy (parent_name/parent_id)
    let systemId: string | undefined;
    let parentTempId: string | undefined;
    const parentSystem = row['Parent System'] || row.parent_system;
    
    if (parentSystem) {
      const { match, availableNames } = findByName(allSystems, parentSystem);
      if (match) {
        systemId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        const available = availableNames.length > 0 ? ` Available systems: ${availableNames.slice(0, 5).join(', ')}${availableNames.length > 5 ? '...' : ''}` : '';
        warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'parent_system', message: `System "${parentSystem}" not found - endpoint will be unassigned.${available}`, severity: 'warning' });
      }
    } else if (row.parent_id) {
      systemId = row.parent_id;
    } else if (row.parent_name) {
      const { match, availableNames } = findByName(allSystems, row.parent_name);
      if (match) {
        systemId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        const available = availableNames.length > 0 ? ` Available: ${availableNames.slice(0, 5).join(', ')}` : '';
        warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'parent_name', message: `System "${row.parent_name}" not found - endpoint will be unassigned.${available}`, severity: 'warning' });
      }
    }
    
    // Check for existing endpoint by name + parent OR by unique identifiers (ip, mac, serial)
    // Only match against real system IDs, not temp IDs
    const ip = row['IP Address'] || row.ip;
    const mac = row['MAC Address'] || row.mac;
    const serial = row['Serial Number'] || row.serial;
    
    const existingEndpoint = existingEndpoints.find(e => {
      // Match by name + parent system (only if real ID)
      if (e.name.toLowerCase() === name.toLowerCase() && systemId && !systemId.startsWith('temp_') && e.system_id === systemId) return true;
      // Match by IP (if provided)
      if (ip && e.ip === ip) return true;
      // Match by MAC (if provided)
      if (mac && e.mac?.toLowerCase() === mac.toLowerCase()) return true;
      // Match by serial (if provided)
      if (serial && e.serial === serial) return true;
      return false;
    });
    
    const backup = row['Has Backup'] || row.backup;
    const assetType = row['Asset Type'] || row.asset_type;
    
    // Network field validation
    const vlanIdRaw = row['VLAN ID'] || row.vlan_id;
    const subnetMask = row['Subnet Mask'] || row.subnet_mask;
    const defaultGateway = row['Default Gateway'] || row.default_gateway;
    
    // Validate IP
    if (ip && !isValidIP(ip)) {
      warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'ip', message: `Invalid IP address: "${ip}"`, severity: 'warning' });
    }
    
    // Validate and format MAC
    let formattedMac = mac;
    if (mac) {
      if (isValidMAC(mac)) {
        formattedMac = formatMAC(mac);
      } else {
        warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'mac', message: `Invalid MAC address: "${mac}"`, severity: 'warning' });
      }
    }
    
    // Validate VLAN ID
    let vlanId: number | undefined;
    if (vlanIdRaw) {
      const vlanNum = parseInt(vlanIdRaw, 10);
      if (isValidVLAN(vlanNum)) {
        vlanId = vlanNum;
      } else {
        warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'vlan_id', message: `Invalid VLAN ID: "${vlanIdRaw}" (must be 1-4094)`, severity: 'warning' });
      }
    }
    
    // Validate subnet mask
    if (subnetMask && !isValidSubnetMask(subnetMask)) {
      warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'subnet_mask', message: `Invalid subnet mask: "${subnetMask}"`, severity: 'warning' });
    }
    
    // Validate default gateway
    if (defaultGateway && !isValidGateway(defaultGateway)) {
      warnings.push({ sheet: 'Endpoints', row: rowNum, column: 'default_gateway', message: `Invalid gateway IP: "${defaultGateway}"`, severity: 'warning' });
    }
    
    const endpoint: ParsedEndpoint = {
      org_id: orgId,
      name,
      type: assetType || 'endpoint',
      system_id: systemId,
      asset_type: assetType || undefined,
      category: row['Category'] || row.category || undefined,
      vendor: row['Vendor'] || row.vendor || undefined,
      model: row['Model'] || row.model || undefined,
      firmware: row['Firmware'] || row.firmware || undefined,
      ip: ip || undefined,
      mac: formattedMac || undefined,
      vlan_id: vlanId,
      subnet_mask: subnetMask || undefined,
      default_gateway: defaultGateway || undefined,
      serial: serial || undefined,
      os: row['Operating System'] || row.os || undefined,
      backup: backup ? backup.toString().toUpperCase() === 'TRUE' : undefined,
      last_patch: row['Last Patch Date'] || row.last_patch || undefined,
      eol_date: row['EOL Date'] || row.eol_date || undefined,
      notes: row['Notes'] || row.notes || undefined,
      _isExisting: !!existingEndpoint,
      _existingId: existingEndpoint?.id,
      _parentTempId: parentTempId,
    };
    
    const rowId = row['ID (auto-generated)'] || row.id;
    if (rowId && !rowId.startsWith('temp_')) endpoint.id = rowId;
    assets.push(endpoint);
  });
  
  return { assets, errors, warnings };
}

// Parse Internet Connections sheet
function parseInternetConnectionsSheet(
  rows: any[], 
  orgId: string, 
  existingConnections: any[],
  allSites: any[],
  allZones: any[],
  allSystems: any[]
) {
  const assets: ParsedInternetConnection[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const label = (row['Label'] || row.label || '').toString().trim();
    if (!label) return;
    
    // Resolve parent
    const parentType = (row['Parent Type'] || row.parent_type || '').toString().toLowerCase();
    const parentName = (row['Parent Name'] || row.parent_name || '').toString().trim();
    
    let siteId: string | undefined;
    let zoneId: string | undefined;
    let systemId: string | undefined;
    let parentTempId: string | undefined;
    
    if (parentType === 'site' && parentName) {
      const { match } = findByName(allSites, parentName);
      if (match) {
        siteId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'Internet Connections', row: rowNum, column: 'parent_name', message: `Site "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else if (parentType === 'zone' && parentName) {
      const { match } = findByName(allZones, parentName);
      if (match) {
        zoneId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'Internet Connections', row: rowNum, column: 'parent_name', message: `Zone "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else if (parentType === 'system' && parentName) {
      const { match } = findByName(allSystems, parentName);
      if (match) {
        systemId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'Internet Connections', row: rowNum, column: 'parent_name', message: `System "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else {
      errors.push({ sheet: 'Internet Connections', row: rowNum, column: 'parent_type', message: 'Parent type and name are required', severity: 'error' });
      return;
    }
    
    // Check for existing
    const existingConnection = existingConnections.find(c => 
      c.label.toLowerCase() === label.toLowerCase() &&
      (c.site_id === siteId || c.zone_id === zoneId || c.system_id === systemId)
    );
    
    const tempId = existingConnection ? undefined : generateTempId();
    
    const connection: ParsedInternetConnection = {
      org_id: orgId,
      label,
      site_id: siteId,
      zone_id: zoneId,
      system_id: systemId,
      connection_type: row['Connection Type'] || row.connection_type || undefined,
      supplier: row['Supplier'] || row['ISP'] || row.supplier || undefined,
      description: row['Description'] || row.description || undefined,
      external_link: row['External Link'] || row.external_link || undefined,
      _isExisting: !!existingConnection,
      _existingId: existingConnection?.id,
      _tempId: tempId,
      _parentTempId: parentTempId,
    };
    
    assets.push(connection);
  });
  
  return { assets, errors, warnings };
}

// Parse DMZ Configurations sheet
function parseDMZConfigsSheet(
  rows: any[], 
  orgId: string, 
  existingConfigs: any[],
  allSites: any[],
  allZones: any[],
  allSystems: any[],
  allCloudServices: any[]
) {
  const assets: ParsedDMZConfig[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const label = (row['Label'] || row.label || '').toString().trim();
    if (!label) return;
    
    // Resolve parent
    const parentType = (row['Parent Type'] || row.parent_type || '').toString().toLowerCase();
    const parentName = (row['Parent Name'] || row.parent_name || '').toString().trim();
    
    let siteId: string | undefined;
    let zoneId: string | undefined;
    let systemId: string | undefined;
    let cloudServiceId: string | undefined;
    let parentTempId: string | undefined;
    
    if (parentType === 'site' && parentName) {
      const { match } = findByName(allSites, parentName);
      if (match) {
        siteId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'DMZ Configurations', row: rowNum, column: 'parent_name', message: `Site "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else if (parentType === 'zone' && parentName) {
      const { match } = findByName(allZones, parentName);
      if (match) {
        zoneId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'DMZ Configurations', row: rowNum, column: 'parent_name', message: `Zone "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else if (parentType === 'system' && parentName) {
      const { match } = findByName(allSystems, parentName);
      if (match) {
        systemId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'DMZ Configurations', row: rowNum, column: 'parent_name', message: `System "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else if ((parentType === 'cloud_service' || parentType === 'cloud service' || parentType === 'cloudservice') && parentName) {
      const { match } = findByName(allCloudServices, parentName);
      if (match) {
        cloudServiceId = match.id || match._tempId;
        if (match._tempId) parentTempId = match._tempId;
      } else {
        errors.push({ sheet: 'DMZ Configurations', row: rowNum, column: 'parent_name', message: `Cloud Service "${parentName}" not found`, severity: 'error' });
        return;
      }
    } else {
      errors.push({ sheet: 'DMZ Configurations', row: rowNum, column: 'parent_type', message: 'Parent type and name are required', severity: 'error' });
      return;
    }
    
    // Check for existing
    const existingConfig = existingConfigs.find(c => 
      c.label.toLowerCase() === label.toLowerCase() &&
      (c.site_id === siteId || c.zone_id === zoneId || c.system_id === systemId || c.cloud_service_id === cloudServiceId)
    );
    
    const tempId = existingConfig ? undefined : generateTempId();
    
    const config: ParsedDMZConfig = {
      org_id: orgId,
      label,
      site_id: siteId,
      zone_id: zoneId,
      system_id: systemId,
      cloud_service_id: cloudServiceId,
      description: row['Description'] || row.description || undefined,
      _isExisting: !!existingConfig,
      _existingId: existingConfig?.id,
      _tempId: tempId,
      _parentTempId: parentTempId,
    };
    
    assets.push(config);
  });
  
  return { assets, errors, warnings };
}

// Parse Firewall Rules sheet
function parseFirewallRulesSheet(
  rows: any[], 
  orgId: string, 
  _existingRules: any[],
  allDMZConfigs: any[]
) {
  const assets: ParsedFirewallRule[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  const validChains = ['INPUT', 'FORWARD', 'OUTPUT'];
  const validActions = ['ALLOW', 'DENY', 'DROP'];
  const validProtocols = ['TCP', 'UDP', 'ICMP', 'ANY'];
  
  rows.forEach((row, idx) => {
    const rowNum = idx + 2;
    
    const dmzName = (row['DMZ Name'] || row.dmz_name || '').toString().trim();
    if (!dmzName) return;
    
    // Find DMZ config (uses label field, not name)
    const { match: dmzMatch, availableLabels } = findByLabel(allDMZConfigs, dmzName);
    if (!dmzMatch) {
      const available = availableLabels.length > 0 ? ` Available: ${availableLabels.slice(0, 5).join(', ')}` : '';
      errors.push({ sheet: 'Firewall Rules', row: rowNum, column: 'dmz_name', message: `DMZ "${dmzName}" not found.${available}`, severity: 'error' });
      return;
    }
    
    const dmzConfigId = dmzMatch.id || dmzMatch._tempId;
    const dmzTempId = dmzMatch._tempId;
    
    const chain = (row['Chain'] || row.chain || 'INPUT').toString().toUpperCase();
    const action = (row['Action'] || row.action || 'ALLOW').toString().toUpperCase();
    const protocol = (row['Protocol'] || row.protocol || 'TCP').toString().toUpperCase();
    
    if (!validChains.includes(chain)) {
      errors.push({ sheet: 'Firewall Rules', row: rowNum, column: 'chain', message: `Invalid chain: "${chain}". Must be INPUT, FORWARD, or OUTPUT`, severity: 'error' });
    }
    
    if (!validActions.includes(action)) {
      errors.push({ sheet: 'Firewall Rules', row: rowNum, column: 'action', message: `Invalid action: "${action}". Must be ALLOW, DENY, or DROP`, severity: 'error' });
    }
    
    if (!validProtocols.includes(protocol)) {
      warnings.push({ sheet: 'Firewall Rules', row: rowNum, column: 'protocol', message: `Unknown protocol: "${protocol}"`, severity: 'warning' });
    }
    
    const ruleOrder = parseInt(row['Rule Order'] || row.rule_order || '0', 10);
    
    const rule: ParsedFirewallRule = {
      org_id: orgId,
      dmz_config_id: dmzConfigId!,
      chain: chain as 'INPUT' | 'FORWARD' | 'OUTPUT',
      source: row['Source'] || row.source || 'any',
      destination: row['Destination'] || row.destination || 'any',
      port: row['Port'] || row.port || undefined,
      protocol: protocol,
      action: action as 'ALLOW' | 'DENY' | 'DROP',
      rule_order: ruleOrder,
      notes: row['Notes'] || row.notes || undefined,
      _isExisting: false, // Rules are always new for simplicity
      _dmzTempId: dmzTempId,
    };
    
    assets.push(rule);
  });
  
  return { assets, errors, warnings };
}
